'use client';

import React, { useEffect, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import AOS from 'aos';
import 'aos/dist/aos.css';

import styles from './CollageGallery.module.scss';
import { Container } from '@mantine/core';
import PresentationOfSection from '../Atoms/PresentationOfSection/PresentationOfSection';
import { AppButton } from '@/src/components/AppButton/AppButton';

export interface CollageItem {
    id: string;
    type: 'image' | 'text';
    src?: string;
    alt?: string;
    text?: string;
    imageTitle?: string;
    size?: 'portrait' | 'landscape' | 'square';
}

interface CollageGalleryProps {
    title?: string;

    /**
     * Keep if you still want a short description under the title (optional).
     * You can also omit it and use `paragraphs` only.
     */
    description?: string;

    /**
     * ✅ Multi-paragraph content rendered as <p>...<p>
     */
    paragraphs?: string[];

    items: CollageItem[];
    backgroundImageUrl?: string;

    /**
     * ✅ Optional CTA button between text and images
     */
    buttonLabel?: string;
    buttonUrl?: string;
    buttonVariant?: 'filled' | 'outline' | 'subtle'; // adapt to your AppButton API
}

const CollageGallery: React.FC<CollageGalleryProps> = ({
    title,
    description,
    paragraphs,
    items,
    backgroundImageUrl,
    buttonLabel,
    buttonUrl,
    buttonVariant = 'filled',
}) => {
    const gridRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const isMobile = window.innerWidth < 1024;

        if (reducedMotion || isMobile) {
            return;
        }

        AOS.init({
            once: true,
            mirror: false,
            duration: 900,
            easing: 'ease-out-cubic',
            offset: 120,
            disableMutationObserver: true,
        });
    }, []);

    useEffect(() => {
        const grid = gridRef.current;
        if (!grid) return;

        const applyDirections = () => {
            const cards = Array.from(grid.querySelectorAll<HTMLElement>('[data-collage-card="true"]'));
            if (cards.length === 0) return;

            // Use offset properties to avoid forced synchronous layout
            const gridLeft = grid.offsetLeft;
            const gridWidth = grid.offsetWidth;

            for (const el of cards) {
                const isText = el.getAttribute('data-collage-type') === 'text';
                if (isText) {
                    el.setAttribute('data-aos', 'fade-up');
                    continue;
                }

                const centerX = el.offsetLeft + el.offsetWidth / 2;
                const rel = (centerX - gridLeft) / Math.max(1, gridWidth);

                if (rel < 0.34) el.setAttribute('data-aos', 'fade-right');
                else if (rel > 0.66) el.setAttribute('data-aos', 'fade-left');
                else el.setAttribute('data-aos', 'fade');
            }
        };

        // Defer to next frame to avoid forced layout during paint
        const raf1 = requestAnimationFrame(() => {
            applyDirections();
            requestAnimationFrame(() => {
                applyDirections();
                AOS.refreshHard();
            });
        });

        const ro = new ResizeObserver(() => {
            requestAnimationFrame(() => {
                applyDirections();
                AOS.refresh();
            });
        });
        ro.observe(grid);

        const imgs = Array.from(grid.querySelectorAll('img'));
        let pending = imgs.length;

        const done = () => {
            pending -= 1;
            if (pending <= 0) {
                requestAnimationFrame(() => {
                    applyDirections();
                    AOS.refreshHard();
                });
            }
        };

        if (imgs.length > 0) {
            imgs.forEach((img) => {
                if ((img as HTMLImageElement).complete) {
                    done();
                    return;
                }
                img.addEventListener('load', done, { once: true });
                img.addEventListener('error', done, { once: true });
            });
        } else {
            requestAnimationFrame(() => {
                applyDirections();
                AOS.refreshHard();
            });
        }

        return () => {
            cancelAnimationFrame(raf1);
            ro.disconnect();
        };
    }, [items]);

    return (
        <Container
            size="xl"
            className={styles.galleryWrapper}
            style={
                backgroundImageUrl
                    ? ({
                        ['--bg-image' as any]: `url(${backgroundImageUrl})`,
                    } as React.CSSProperties)
                    : undefined
            }
        >
            <PresentationOfSection
                title={title}
                description={description}
                align="center"
                aos={{ animation: 'fade-up', delay: 0 }}
            />

            {/* ✅ Multi-paragraph block */}
            {paragraphs?.length ? (
                <div className={styles.paragraphs} data-aos="fade-up" data-aos-delay="80">
                    {paragraphs.map((p, i) => (
                        <p key={i} className={styles.paragraph}>
                            {p}
                        </p>
                    ))}
                </div>
            ) : null}

            {/* ✅ Optional AppButton between text and images */}
            {buttonLabel && buttonUrl ? (
                <div className={styles.midButtonWrapper} data-aos="fade-up" data-aos-delay="140">
                    <AppButton
                        component={Link as any}
                        href={buttonUrl}
                        variant={buttonVariant as any}
                    >
                        {buttonLabel}
                    </AppButton>
                </div>
            ) : null}

            <div className={styles.squareWrapper}>
                <div className={styles.grid} ref={gridRef}>
                    {items.map((item, idx) => {
                        const sizeClass =
                            item.size === 'portrait'
                                ? styles.portrait
                                : item.size === 'landscape'
                                    ? styles.landscape
                                    : styles.square;

                        return (
                            <article
                                key={item.id}
                                data-collage-card="true"
                                data-collage-type={item.type}
                                className={`${styles.card} ${sizeClass}`}
                                data-aos="fade-up"
                                data-aos-anchor-placement="top-bottom"
                            >
                                {item.type === 'image' && item.src ? (
                                    <figure className={styles.imageCard}>
                                        <Image
                                            src={item.src}
                                            alt={item.alt || item.imageTitle || 'Artwork'}
                                            fill
                                            sizes="(max-width: 600px) 100vw, (max-width: 1024px) 50vw, 33vw"
                                            className={styles.image}
                                            priority={idx === 0}
                                            quality={80}
                                        />
                                        {item.imageTitle && (
                                            <figcaption className={styles.label}>{item.imageTitle}</figcaption>
                                        )}
                                    </figure>
                                ) : (
                                    <div className={styles.textCard}>
                                        <p className={styles.text}>{item.text}</p>
                                    </div>
                                )}
                            </article>
                        );
                    })}
                </div>
            </div>
        </Container>
    );
};

export default CollageGallery;